import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { useAuth, vortxTypography } from '@vortx/shared-ui';
import { useRetroContext } from '@vortx/shared-ui';
import { useResponsive } from '../../hooks/useResponsive';
import { useBootContext } from '@vortx/shared-ui';
import ResponsiveDrawer from '../ResponsiveDrawer';
import RetroWireframeGlobe from './RetroWireframeGlobe';
import RetroSecurityBadge from './RetroSecurityBadge';
import RetroNavigation from './RetroNavigation';
import RetroTelemetry from './RetroTelemetry';
import RetroActivityFeed from './RetroActivityFeed';
import RetroStatusBar from './RetroStatusBar';

const RetroLayout = () => {
    const location = useLocation();
    const { currentUser, userProfile } = useAuth();
    const {
        currentApp,
        appConfig,
        isTransitioning,
        transitionToApp,
        switchAppOnly,
        activeProject,
        clearProjectScope,
        currentProject,
        currentScenario,
        scenarioTab,
        setScenarioTab,
        phdSection,
        setPhdSection,
        evSection,
        setEvSection,
        setEvScenarioTab,
        coldSection,
        setColdSection,
    } = useRetroContext();

    const isDemo = userProfile?.is_demo;
    const isAdmin = !isDemo && (userProfile?.is_super_admin || userProfile?.is_company_admin);
    const isSuperAdmin = userProfile?.is_super_admin;
    const isInAdminSection = location.pathname.startsWith('/admin');

    const { isLargeDesktop } = useResponsive();
    const { panelPower } = useBootContext();

    const getPowerStyle = (power) => {
        if (power >= 1) return {};
        return {
            opacity: power > 0 ? Math.min(power * 1.5, 1) : 0,
            boxShadow: power > 0.3
                ? `inset 0 0 ${power * 30}px rgba(63, 106, 100, ${power * 0.1})`
                : 'none',
            filter: power < 0.8 ? `brightness(${0.5 + power * 0.6})` : undefined,
            transition: 'none',
        };
    };

    // Drawer state for narrow screens
    const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

    // Close drawers on route change
    useEffect(() => {
        setLeftDrawerOpen(false);
        setRightDrawerOpen(false);
    }, [location.pathname]);

    // Close drawers when resizing to desktop
    useEffect(() => {
        if (isLargeDesktop) {
            setLeftDrawerOpen(false);
            setRightDrawerOpen(false);
        }
    }, [isLargeDesktop]);

    // Only one drawer open at a time
    const handleLeftToggle = () => {
        setRightDrawerOpen(false);
        setLeftDrawerOpen(prev => !prev);
    };

    const handleRightToggle = () => {
        setLeftDrawerOpen(false);
        setRightDrawerOpen(prev => !prev);
    };

    // Sync route changes with retro context
    useEffect(() => {
        const path = location.pathname;
        console.warn('[DEBUG] RetroLayout: route sync effect running', { path, currentApp });

        // Determine the target app from the route
        let targetApp = null;
        if (path === '/' || path === '/dashboard') {
            targetApp = 'dashboard';
        } else if (path.startsWith('/all-projects')) {
            targetApp = 'all-projects';
        } else if (path.startsWith('/projects') || path.startsWith('/scenario')) {
            targetApp = 'site-dna';
        } else if (path.startsWith('/ems')) {
            targetApp = 'flux-ems';
        } else if (path.startsWith('/phd')) {
            targetApp = 'flux-phd';
        } else if (path.startsWith('/ev')) {
            targetApp = 'flux-ev';
        } else if (path.startsWith('/cold-storage')) {
            targetApp = 'cold-storage';
        } else if (path.startsWith('/admin')) {
            targetApp = 'admin';
        }

        if (targetApp) {
            // Clear stale project scope when user lands on the project list
            if (path === '/all-projects' && activeProject) {
                clearProjectScope();
            }

            // When activeProject is set, switch apps without clearing project scope
            if (activeProject && path !== '/all-projects') {
                switchAppOnly(targetApp);
            } else {
                transitionToApp(targetApp);
            }
        }
    }, [location.pathname, transitionToApp, switchAppOnly, activeProject, clearProjectScope, currentApp]);

    // Height calculation: viewport - header (64px) - status bar (50px)
    const panelHeight = 'calc(100vh - 64px - 50px)';

    return (
        <Box sx={{
            height: panelHeight,
            overflow: 'hidden',
        }}>
            {/* Main Layout */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: isLargeDesktop
                    ? 'minmax(220px, 16%) 1fr minmax(260px, 20%)'
                    : '1fr',
                gap: 0,
                height: '100%',
            }}>
                {/* Left Panel - Navigation (flex layout for bottom anchor) */}
                <ResponsiveDrawer
                    anchor="left"
                    open={leftDrawerOpen}
                    onClose={() => setLeftDrawerOpen(false)}
                    onToggle={handleLeftToggle}
                    desktopSx={{
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        borderRight: '1px solid rgba(63, 106, 100, 0.3)',
                        padding: { md: 1.5, lg: 2 },
                        background: 'rgba(0, 0, 0, 0.2)',
                        height: '100%',
                        minHeight: 0,
                        // Custom scrollbar styling for retro feel
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(178, 140, 80, 0.3)',
                            borderRadius: '3px',
                            '&:hover': {
                                background: 'rgba(178, 140, 80, 0.5)',
                            },
                        },
                        ...getPowerStyle(panelPower.left),
                    }}
                >
                    {/* Fixed Top Section - Globe + Badge */}
                    <Box sx={{ flexShrink: 0 }}>
                        {/* Wireframe Globe */}
                        <Box sx={{ mb: 2, pt: 1 }}>
                            <RetroWireframeGlobe size={100} color={appConfig.color} />
                            <Typography sx={{
                                fontFamily: vortxTypography.heading,
                                fontSize: '0.85rem',
                                color: appConfig.color,
                                textAlign: 'center',
                                mt: 3,
                                textTransform: 'uppercase',
                                letterSpacing: '0.15em',
                                textShadow: `0 0 10px ${appConfig.color}40`,
                                opacity: isTransitioning ? 0 : 1,
                                transition: 'opacity 0.2s ease, color 0.3s ease',
                            }}>
                                Flux
                            </Typography>
                        </Box>

                        {/* Security Badge */}
                        <Box sx={{
                            opacity: isTransitioning ? 0 : 1,
                            transform: isTransitioning ? 'translateY(-10px)' : 'translateY(0)',
                            transition: 'opacity 0.2s ease, transform 0.2s ease',
                            mb: 2,
                        }}>
                            <RetroSecurityBadge
                                userName={userProfile?.display_name || currentUser?.displayName || currentUser?.email?.split('@')[0]}
                                userEmail={currentUser?.email}
                                userPhoto={userProfile?.avatar_url || currentUser?.photoURL}
                                isAdmin={isAdmin}
                            />
                        </Box>
                    </Box>

                    {/* Scrollable Navigation Section */}
                    <Box sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        opacity: isTransitioning ? 0 : 1,
                        transition: 'opacity 0.2s ease',
                        // Scrollbar styling
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(178, 140, 80, 0.3)',
                            borderRadius: '3px',
                            '&:hover': {
                                background: 'rgba(178, 140, 80, 0.5)',
                            },
                        },
                    }}>
                        <RetroNavigation
                            currentApp={currentApp}
                            scenarioTab={scenarioTab}
                            onScenarioTabChange={setScenarioTab}
                            phdSection={phdSection}
                            onPhdSectionChange={setPhdSection}
                            evSection={evSection}
                            onEvSectionChange={setEvSection}
                            onEvScenarioTabChange={setEvScenarioTab}
                            coldSection={coldSection}
                            onColdSectionChange={setColdSection}
                            appConfig={appConfig}
                            isAdmin={isAdmin}
                            isSuperAdmin={isSuperAdmin}
                        />
                    </Box>

                    {/* Fixed Bottom Section - Admin Button (hide when in admin) */}
                    {isAdmin && !isInAdminSection && (
                        <Box
                            onClick={() => window.location.href = '/admin'}
                            sx={{
                                flexShrink: 0,
                                mt: 'auto',
                                pt: 2,
                                padding: 1.5,
                                background: currentApp === 'admin'
                                    ? 'rgba(231, 76, 60, 0.2)'
                                    : 'rgba(231, 76, 60, 0.1)',
                                border: `1px solid ${currentApp === 'admin' ? '#e74c3c' : 'rgba(231, 76, 60, 0.3)'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: '#e74c3c',
                                    background: 'rgba(231, 76, 60, 0.15)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AdminIcon sx={{ fontSize: 16, color: '#e74c3c' }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    color: '#e74c3c',
                                }}>
                                    Admin Panel
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </ResponsiveDrawer>

                {/* Center - Main Content (scrollable) - CRT Screen Effect */}
                <Box sx={{
                    padding: 3,
                    opacity: isTransitioning ? 0.3 : 1,
                    transition: 'opacity 0.15s ease',
                    height: '100%',
                    overflowY: 'auto',
                    // Charcoal CRT screen background
                    background: 'linear-gradient(180deg, #2c2c2c 0%, #252525 50%, #1e1e1e 100%)',
                    // Subtle inner shadow for screen depth
                    boxShadow: 'inset 0 0 60px rgba(0, 0, 0, 0.4), inset 0 0 20px rgba(0, 0, 0, 0.3)',
                    // Custom scrollbar styling
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(0, 0, 0, 0.3)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(95, 212, 196, 0.3)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(95, 212, 196, 0.5)',
                        },
                    },
                    ...getPowerStyle(panelPower.content),
                }}>
                    <Outlet />
                </Box>

                {/* Right Panel - Telemetry + Activity Feed */}
                <ResponsiveDrawer
                    anchor="right"
                    open={rightDrawerOpen}
                    onClose={() => setRightDrawerOpen(false)}
                    onToggle={handleRightToggle}
                    desktopSx={{
                        display: { xs: 'none', lg: 'flex' },
                        flexDirection: 'column',
                        borderLeft: '1px solid rgba(63, 106, 100, 0.3)',
                        padding: { md: 1.5, lg: 2 },
                        background: 'rgba(0, 0, 0, 0.2)',
                        height: '100%',
                        minHeight: 0,
                        overflowY: 'auto',
                        // Custom scrollbar styling
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.2)',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(178, 140, 80, 0.3)',
                            borderRadius: '3px',
                            '&:hover': {
                                background: 'rgba(178, 140, 80, 0.5)',
                            },
                        },
                        ...getPowerStyle(panelPower.right),
                    }}
                >
                    {/* Telemetry Section (moved from left panel) */}
                    <Box sx={{
                        flexShrink: 0,
                        mb: 2,
                        opacity: isTransitioning ? 0 : 1,
                        transition: 'opacity 0.2s ease',
                    }}>
                        <RetroTelemetry appConfig={appConfig} />
                    </Box>

                    {/* Activity Feed */}
                    <Box sx={{
                        flex: 1,
                        opacity: isTransitioning ? 0 : 1,
                        transform: isTransitioning ? 'translateY(-10px)' : 'translateY(0)',
                        transition: 'opacity 0.2s ease, transform 0.2s ease',
                    }}>
                        <RetroActivityFeed
                            appConfig={appConfig}
                            currentProject={currentProject}
                        />
                    </Box>

                </ResponsiveDrawer>
            </Box>

            {/* Status Bar */}
            <Box sx={getPowerStyle(panelPower.footer)}>
                <RetroStatusBar
                    currentApp={currentApp}
                    appConfig={appConfig}
                    userEmail={currentUser?.email}
                    currentProject={currentProject}
                    currentScenario={currentScenario}
                />
            </Box>
        </Box>
    );
};

export default RetroLayout;
